// Session replay on every page but /meet, at the project's sampled rate
// (clients/posthog.ts). /meet records only a Call Guidance call, and its own
// controller (components/meet/replay/controller.tsx) owns starting and
// stopping there, so this hook stops before a route enters /meet and starts
// again after one leaves it. It never touches the recorder while on /meet,
// never records a route whose path is a credential (utilities/secret-urls),
// and never restarts on a page that has recorded a /meet call.
import { useRouter } from 'next/router'
import { useEffect } from 'react'

import posthog, { isLocalReplayMode, isPostHogEnabled } from 'clients/posthog'
import {
  isAppReplayExcludedPath,
  meetReplayUsedThisPage,
} from 'clients/meet-replay-privacy'

// Whether this hook started the recorder in this page lifetime.
let appRecording = false

/** Tests only: forget whether app-wide replay is running. */
export const resetAppReplayForTests = () => {
  appRecording = false
}

/** The path of a Next route URL, without its query or hash. */
const pathOf = (url: string) => url.split(/[?#]/)[0]

/** Starts sampled replay off /meet; stops it before a route enters /meet. */
const useAppReplay = () => {
  const router = useRouter()

  useEffect(() => {
    // Local replay (clients/posthog-local-replay) records only a /meet call.
    if (!isPostHogEnabled() || isLocalReplayMode()) return undefined
    const start = () => {
      if (
        appRecording ||
        isAppReplayExcludedPath(window.location.pathname) ||
        // A page that recorded a /meet call shares its PostHog session with
        // that recording; nothing else joins it before a reload.
        meetReplayUsedThisPage()
      )
        return
      appRecording = true
      // No override: the sample rate, minimum duration and project settings
      // decide whether this session uploads.
      posthog.startSessionRecording()
    }
    const leaveFor = (url: string) => {
      if (!appRecording || !isAppReplayExcludedPath(pathOf(url))) return
      appRecording = false
      posthog.stopSessionRecording()
    }
    start()
    router.events.on('routeChangeStart', leaveFor)
    router.events.on('routeChangeComplete', start)
    // A navigation into /meet that fails leaves the page where it was: resume
    // there (start() reads the current path, so it stays off on /meet).
    router.events.on('routeChangeError', start)
    return () => {
      router.events.off('routeChangeStart', leaveFor)
      router.events.off('routeChangeComplete', start)
      router.events.off('routeChangeError', start)
    }
  }, [router.events])
}

export default useAppReplay
